import { PresenceTransition, Stack, VStack } from 'native-base';
import { FormDataIsLoading } from 'ui/components/FormBuilderV2/components/FormDataIsLoading';
import FormHeader from 'ui/components/FormBuilderV2/components/FormHeader';
import Radio from 'ui/components/Radio';
import { LocationOptionsEnum } from 'ui/enums';
import { colors } from 'ui/theme/colors';
import { ActivityType } from 'ui/types/activities';

import { FromHome } from '~/components/FromHome';
import { locationOptions } from '~/constants/forms.constants';

import { EditWhenWhereFormProps } from '../../../types';
import { useWhenWhereFormController } from './controller';
import { MultipleLocations } from './MultipleLocations';
import { SingleLocation } from './SingleLocation';

export const WhenWhereForm = ({
  isTeamEvent,
  isLoadingDefaultValues,
  handleContinue,
  isLoading,
  defaultLocationGroupsByDate,
  ...rest
}: EditWhenWhereFormProps) => {
  const { handleBack, control, currentLocationType, defaultValues } =
    useWhenWhereFormController({
      isTeamEvent,
      handleContinue,
      isLoading,
      ...rest,
    });

  const formsByLocationType = () => {
    return {
      [LocationOptionsEnum.SingleLocation]: (
        <PresenceTransition
          visible={currentLocationType === LocationOptionsEnum.SingleLocation}
          style={{
            width: '100%',
          }}
          initial={{ opacity: 0, scaleX: 0.9 }}
          animate={{ opacity: 1, scaleX: 1, transition: { duration: 300 } }}
        >
          <SingleLocation
            handleBack={handleBack}
            handleContinue={handleContinue}
            isTeamEvent={isTeamEvent}
            isLoading={isLoading}
            defaultValues={defaultValues}
          />
        </PresenceTransition>
      ),
      [LocationOptionsEnum.MultipleLocations]: (
        <PresenceTransition
          visible={
            currentLocationType === LocationOptionsEnum.MultipleLocations
          }
          style={{
            width: '100%',
          }}
          initial={{ opacity: 0, scaleX: 0.9 }}
          animate={{ opacity: 1, scaleX: 1, transition: { duration: 300 } }}
        >
          <MultipleLocations
            handleBack={handleBack}
            handleContinue={handleContinue}
            isTeamEvent={isTeamEvent}
            isLoading={isLoading}
            defaultValues={defaultValues}
            defaultLocationGroupsByDate={defaultLocationGroupsByDate}
          />
        </PresenceTransition>
      ),
      [LocationOptionsEnum.FromHome]: (
        <PresenceTransition
          visible={currentLocationType === LocationOptionsEnum.FromHome}
          style={{
            width: '100%',
          }}
          initial={{ opacity: 0, scaleX: 0.9 }}
          animate={{ opacity: 1, scaleX: 1, transition: { duration: 300 } }}
        >
          <FromHome
            handleContinue={handleContinue}
            handleBack={handleBack}
            activityType={ActivityType.Event}
            defaultValues={defaultValues}
            isLoading={isLoading}
            isEditing={true}
          />
        </PresenceTransition>
      ),
      [LocationOptionsEnum.InPerson]: null, // Avoid Lint error for now
    }[currentLocationType];
  };

  return isLoadingDefaultValues ? (
    <FormDataIsLoading />
  ) : (
    <VStack w="100%" space={4}>
      <FormHeader
        title="When and where"
        subTitle="Specify the time and place where the event will take place."
      />

      <VStack>
        <Stack space={6} pt="6">
          <Radio
            label="Location options"
            isRequired
            control={control}
            name="locationOption"
            flexDirection="row"
            checkedColor={colors.primary[600]}
            options={locationOptions}
            defaultValue={currentLocationType}
          />
        </Stack>

        <Stack minH="40vh">{formsByLocationType()}</Stack>
      </VStack>
    </VStack>
  );
};
